import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MemberIdentifier } from '@app/core/_models/member-interface';
import { MemberService } from '@app/core/members/member.service';
import { ActivatedRoute } from '@angular/router';
import { LoginService } from '@app/+login/login.service';
import { SessionService } from '@app/core/session/session.service';
import { HttpErrorResponse } from '@angular/common/http';
import storage from 'local-storage-fallback';
import {
  LOGIN_ID_STORAGE_ITEM_KEY,
  PASSWORD_INVALID_ERROR_KEY,
  PASSWORD_REQUIRED_ERROR_KEY,
} from '@app/+login/deprecated-hybrid/consts';
import { saveIdentifierToStorage } from '@app/+login/deprecated-hybrid/helpers';
import { TranslatePipe } from '@ngx-translate/core';
import { NgClass } from '@angular/common';
import { InputErrorsComponent } from '@app/shared/components/input-errors/input-errors.component';

@Component({
  selector: 'app-email-otp',
  templateUrl: './email-otp.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslatePipe,
    NgClass,
    ReactiveFormsModule,
    InputErrorsComponent,
  ],
})
export class EmailOTPComponent implements OnInit, OnDestroy {
  @Input() public identifier?: MemberIdentifier;

  public form = this.formBuilder.group({
    password: ['', Validators.required],
  });
  public formErrors: { error?: string } = {};
  public passwordSendComplete = false;
  public passwordSendSuccess = false;
  public isSendingPassword = false;
  public submitted = false;

  private unsubscribe$ = new Subject();

  constructor(private formBuilder: UntypedFormBuilder,
              private memberService: MemberService,
              private route: ActivatedRoute,
              private loginService: LoginService,
              private sessionService: SessionService,
              private cdr: ChangeDetectorRef) { }

  public ngOnInit(): void {
    this.sendPassword();
    this.clearFormErrorsOnValueChange();
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }

  public onSubmit(): void {
    this.submitted = true;

    if (this.form.get('password')?.invalid) {
      this.formErrors = { error: PASSWORD_REQUIRED_ERROR_KEY };
      this.cdr.detectChanges();
      return;
    }
    this.signIn();
  }

  public sendPassword(): void {
    if (this.identifier) {
      saveIdentifierToStorage(this.identifier);
    }
    this.isSendingPassword = true;

    this.memberService.sendPasswordByEmail(this.identifier)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (clientSecret) => this.sendOTPSuccess(clientSecret),
        error: () => this.sendOTPFailure(),
      });
    this.passwordSendComplete = false;
    this.cdr.detectChanges();
  }

  private signIn(): void {
    this.sessionService
      .deprecatedSignIn(this.identifier, this.form.value.password.trim(), this.sessionService.getOtpSecret())
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => this.signInSuccess(), err => this.signInFailure(err));
  }

  private sendOTPSuccess(clientSecret: string): void {
    this.isSendingPassword = false;
    this.passwordSendComplete = true;
    this.passwordSendSuccess = true;
    this.sessionService.saveOtpSecret(clientSecret);
    this.cdr.detectChanges();
  }

  private sendOTPFailure(): void {
    this.isSendingPassword = false;
    this.passwordSendComplete = true;
    this.passwordSendSuccess = false;
    this.cdr.detectChanges();
  }

  private signInSuccess(): void {
    this.loginService.afterSignedIn(this.route.parent);
    storage.removeItem(LOGIN_ID_STORAGE_ITEM_KEY);
  }

  private signInFailure(err: HttpErrorResponse): void {
    if (err.status === 401) {
      this.formErrors = { error: PASSWORD_INVALID_ERROR_KEY };
      this.cdr.detectChanges();
    }
  }

  private clearFormErrorsOnValueChange(): void {
    this.form.valueChanges.pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(() => this.formErrors = {});
  }
}
